/**
 * Morse — das Merkwort eines Zeichens als kleines, ruhiges Element. (15.10.2026, Martin: „Ich brauche einen Anhalt, dass ich
 * das richtig erkenne.“)
 *
 * Die Daten kommen allein aus shared/morse-merkwoerter.ts (geprüft nach der o-Regel). Hier nur die Form: die Silben mit
 * Bindestrich, die LANGEN Silben (mit „o“ = Strich) fett und unterstrichen — Form, nicht nur Farbe. Ziffern bekommen ihre
 * Bauregel als Satz („1 Punkt, dann 4 Striche“). Zeichen ohne geprüfte Merkhilfe (Satzzeichen, Umlaute) bekommen nichts.
 *
 * Vorlesen: Vor dem Wort steht nur für Bildschirmleser „Merkwort“. Wo ein Element sich selbst ein `aria-label` gibt
 * (Kachel, Tabellenkarte), hängt `merkwortAria` den Satz daran.
 *
 * ⚠️ Keine Inline-Styles: alles in style.css (`.morse-merk…`).
 */
import { merkhilfeText, morseMerkhilfe } from "../../shared/morse-merkwoerter.js";
import { MERKWORT } from "./morse-texte.js";
import { el } from "./morse-teile.js";

/** Das Merkwort als Element — `null`, wo es keine geprüfte Merkhilfe gibt. `klasse` kommt zu `morse-merk` dazu. */
export function merkwortElement(zeichen: string, klasse?: string): HTMLElement | null {
  const m = morseMerkhilfe(zeichen);
  if (!m) return null;
  const e = el("span", klasse ? `morse-merk ${klasse}` : "morse-merk");
  e.dataset.art = m.art;
  e.appendChild(el("span", "morse-nur-vorlesen", `${MERKWORT.vorlesen} `));
  if (m.art === "regel") {
    e.append(m.text);
    return e;
  }
  // Das Wort so, wie es geschrieben ist (Bindestriche, Leerzeichen, Komma bleiben) — je Silbe ein Element mit ihrer Länge,
  // in derselben Reihenfolge wie `m.silben` (die Trennung ist dieselbe wie in merkwortSilben).
  let i = 0;
  for (const stueck of m.merkwort.split(/(-|\s+)/)) {
    if (stueck === "-" || /^\s+$/.test(stueck) || !/\p{L}/u.test(stueck)) {
      e.append(stueck);
      continue;
    }
    const silbe = el("span", "morse-merk-silbe", stueck);
    if (m.silben[i++]?.lang) silbe.dataset.lang = "true";
    e.appendChild(silbe);
  }
  return e;
}

/** Der Satz fürs `aria-label` („Merkwort Re-tor-te“) — leer, wo es keins gibt. */
export function merkwortAria(zeichen: string): string {
  const m = morseMerkhilfe(zeichen);
  return m ? MERKWORT.aria(merkhilfeText(m)) : "";
}
